import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDock } from '@/contexts/DockContext';
import { DockTabs } from '@/components/UtilityDock';
import { toast } from 'sonner';
import { MessageCircle, X, Send, ChevronLeft, Hash, FolderOpen, Pencil, Trash2, Check, Clapperboard, AtSign, SmilePlus } from 'lucide-react';

const channelKey = (c) => `${c.scope}:${c.ref_id}`;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '✅', '👀', '🔥', '👏'];

export function ChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab, setTab, setChatUnread } = useDock();
  const open = tab === 'chat';
  const setOpen = (v) => setTab((typeof v === 'function' ? v(open) : v) ? 'chat' : null);
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null); // {scope, ref_id, name}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState({ total: 0, channels: {}, mention_total: 0, mentions: {} });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [members, setMembers] = useState([]);
  const [projScenes, setProjScenes] = useState([]);
  const [sceneRefs, setSceneRefs] = useState([]); // [{id,title}]
  const [showScenePicker, setShowScenePicker] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [highlightId, setHighlightId] = useState(null);
  const [reactionPickerId, setReactionPickerId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const projectMatch = location.pathname.match(/^\/project\/([^/]+)/);
  const currentProjectId = projectMatch ? projectMatch[1] : null;

  const loadUnread = useCallback(async () => {
    try { setUnread((await api.get('/chat/unread')).data); } catch { /* ignore */ }
  }, []);

  const loadChannels = useCallback(async () => {
    try { const r = await api.get('/chat/channels'); setChannels(r.data.channels || []); return r.data.channels || []; }
    catch { return []; }
  }, []);

  const loadMessages = useCallback(async (ch) => {
    if (!ch) return;
    try { setMessages((await api.get(`/chat/${ch.scope}/${ch.ref_id}/messages`)).data.messages || []); }
    catch { /* ignore */ }
  }, []);

  const markRead = useCallback(async (ch) => {
    if (!ch) return;
    try { await api.post(`/chat/${ch.scope}/${ch.ref_id}/read`); loadUnread(); window.dispatchEvent(new Event('mentions-updated')); } catch { /* ignore */ }
  }, [loadUnread]);

  useEffect(() => {
    if (!user) return;
    loadUnread();
    const i = setInterval(loadUnread, 4000);
    return () => clearInterval(i);
  }, [user, loadUnread]);

  // Jump to a specific channel/message from the notifications bell
  useEffect(() => {
    const handler = (e) => {
      const { scope, ref_id, name, messageId } = e.detail || {};
      if (!scope || !ref_id) return;
      setOpen(true);
      setActive({ scope, ref_id, name: name || (scope === 'workspace' ? 'General' : 'Project') });
      setHighlightId(messageId || null);
    };
    window.addEventListener('open-chat-channel', handler);
    return () => window.removeEventListener('open-chat-channel', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const chs = await loadChannels();
      let target = null;
      if (currentProjectId) target = chs.find(c => c.scope === 'project' && c.ref_id === currentProjectId);
      if (!target) target = chs.find(c => c.scope === 'workspace') || chs[0] || null;
      setActive(target);
    })();
  }, [open, currentProjectId, loadChannels]);

  // Load members (for @mentions) + scenes (for scene refs) when the channel changes
  useEffect(() => {
    setSceneRefs([]); setShowScenePicker(false); setMentionOpen(false);
    if (!active) { setMembers([]); setProjScenes([]); return; }
    (async () => {
      try { setMembers((await api.get(`/chat/${active.scope}/${active.ref_id}/members`)).data.members || []); } catch { setMembers([]); }
      if (active.scope === 'project') {
        try { setProjScenes((await api.get(`/projects/${active.ref_id}/scenes`)).data || []); } catch { setProjScenes([]); }
      } else { setProjScenes([]); }
    })();
  }, [active]);

  useEffect(() => {
    if (!open || !active) return;
    loadMessages(active);
    markRead(active);
    const i = setInterval(() => { loadMessages(activeRef.current); markRead(activeRef.current); }, 3000);
    return () => clearInterval(i);
  }, [open, active, loadMessages, markRead]);

  useEffect(() => {
    if (highlightId) {
      const el = document.querySelector(`[data-testid="chat-message-${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const t = setTimeout(() => setHighlightId(null), 2500);
        return () => clearTimeout(t);
      }
    } else if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, active, highlightId]);

  const onChangeText = (val) => {
    setText(val);
    const m = val.match(/@([^@\n]*)$/);
    if (m && members.length) { setMentionQuery(m[1]); setMentionOpen(true); setMentionIndex(0); }
    else setMentionOpen(false);
  };

  const pickMention = (member) => {
    setText(prev => prev.replace(/@([^@\n]*)$/, `@${member.name} `));
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const collectMentions = (body) => members.filter(mem => new RegExp(`@${escapeRe(mem.name)}(?!\\w)`).test(body)).map(mem => mem.user_id);

  const send = async () => {
    const t = text.trim();
    if ((!t && sceneRefs.length === 0) || !active || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/chat/${active.scope}/${active.ref_id}/messages`, {
        text: t || ' ', mentions: collectMentions(t), scene_refs: sceneRefs,
      });
      setMessages(prev => [...prev, res.data]);
      setText(''); setSceneRefs([]); setMentionOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const saveEdit = async (id) => {
    const t = editText.trim();
    if (!t) return;
    try {
      const res = await api.put(`/chat/messages/${id}`, { text: t, mentions: collectMentions(t) });
      setMessages(prev => prev.map(m => m.id === id ? res.data : m));
      setEditingId(null); setEditText('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to edit'); }
  };

  const del = async (id) => {
    try { await api.delete(`/chat/messages/${id}`); setMessages(prev => prev.map(m => m.id === id ? { ...m, deleted: true, text: '', scene_refs: [], reactions: {} } : m)); }
    catch { toast.error('Failed to delete'); }
  };

  const react = async (id, emoji) => {
    setReactionPickerId(null);
    try {
      const res = await api.post(`/chat/messages/${id}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === id ? res.data : m));
    } catch { toast.error('Failed to react'); }
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  const renderText = (body) => {
    if (!body) return body;
    const names = members.map(m => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
    if (!names.length) return body;
    const re = new RegExp('@(' + names.map(escapeRe).join('|') + ')(?!\\w)', 'g');
    const parts = []; let last = 0; let m;
    while ((m = re.exec(body)) !== null) {
      if (m.index > last) parts.push(body.slice(last, m.index));
      const isMe = members.find(mem => mem.name === m[1])?.user_id === user.id;
      parts.push(<span key={m.index} className={isMe ? 'text-[#FF4500] font-semibold' : 'text-[#A78BFA] font-semibold'}>@{m[1]}</span>);
      last = m.index + m[0].length;
    }
    if (last < body.length) parts.push(body.slice(last));
    return parts;
  };

  const goToScene = (sceneId) => { if (active?.scope === 'project') { setOpen(false); navigate(`/project/${active.ref_id}/scenes/${sceneId}`); } };

  const toggleSceneRef = (s) => {
    setSceneRefs(prev => prev.find(r => r.id === s.id) ? prev.filter(r => r.id !== s.id) : [...prev, { id: s.id, title: s.title }]);
  };

  const filteredMembers = members.filter(m => m.user_id !== user?.id && m.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6);
  const memberNameMap = members.reduce((acc, m) => { acc[m.user_id] = m.name; return acc; }, {});

  useEffect(() => { setChatUnread(unread.total || 0); }, [unread.total, setChatUnread]);

  if (!user) return null;
  const hasMentions = unread.mention_total > 0;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)}>
          <div
            data-testid="chat-panel"
            data-has-mentions={hasMentions ? '1' : '0'}
            onClick={e => e.stopPropagation()}
            className="absolute right-2 md:right-4 bottom-56 md:bottom-40 w-[calc(100vw-1rem)] max-w-[380px] h-[70vh] max-h-[560px] bg-[#0d0d11] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] shrink-0">
              {active && channels.length > 1 && (
                <button data-testid="chat-back-btn" onClick={() => setActive(null)} className="p-1 -ml-1 rounded hover:bg-white/5 text-[#8A8A93]">
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                {active ? (active.scope === 'workspace' ? <Hash className="w-4 h-4 text-[#FF4500] shrink-0" /> : <FolderOpen className="w-4 h-4 text-[#FF4500] shrink-0" />) : <MessageCircle className="w-4 h-4 text-[#FF4500] shrink-0" />}
                <span className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {active ? active.name : 'Messages'}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2"><DockTabs /></div>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/5 text-[#8A8A93]" data-testid="chat-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!active ? (
              <div className="flex-1 overflow-y-auto p-2" data-testid="chat-channel-list">
                {channels.length === 0 ? (
                  <p className="text-center text-xs text-[#55555C] py-8">No channels available.</p>
                ) : channels.map(c => {
                  const n = unread.channels[channelKey(c)] || 0;
                  const mn = unread.mentions?.[channelKey(c)] || 0;
                  return (
                    <button key={channelKey(c)} data-testid={`chat-channel-${c.scope}-${c.ref_id}`} onClick={() => setActive(c)}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left transition-colors">
                      {c.scope === 'workspace' ? <Hash className="w-4 h-4 text-[#8A8A93] shrink-0" /> : <FolderOpen className="w-4 h-4 text-[#8A8A93] shrink-0" />}
                      <span className="text-sm text-white truncate flex-1">{c.name}</span>
                      {mn > 0 && <AtSign className="w-3.5 h-3.5 text-[#FF4500]" data-testid={`chat-mention-dot-${c.scope}-${c.ref_id}`} />}
                      {n > 0 && <span className="min-w-[18px] h-4.5 px-1.5 rounded-full bg-[#FF4500] text-white text-[10px] font-bold flex items-center justify-center">{n}</span>}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
                  {messages.length === 0 ? (
                    <p className="text-center text-xs text-[#55555C] py-8">No messages yet. Say hello 👋</p>
                  ) : messages.map(m => {
                    const mine = m.user_id === user.id;
                    return (
                      <div key={m.id} className={`group flex flex-col ${mine ? 'items-end' : 'items-start'} ${highlightId === m.id ? 'rounded-lg ring-2 ring-[#FF4500]/60 -mx-1 px-1 py-1 transition-all' : ''}`} data-testid={`chat-message-${m.id}`}>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          {!mine && <span className="text-[11px] font-medium text-[#A78BFA]">{m.user_name}</span>}
                          <span className="text-[10px] text-[#55555C]">{fmtTime(m.created_at)}{m.edited_at ? ' · edited' : ''}</span>
                        </div>
                        {editingId === m.id ? (
                          <div className="flex items-center gap-1 w-full">
                            <input value={editText} onChange={e => setEditText(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEdit(m.id)}
                              className="flex-1 px-2 py-1 rounded-lg bg-[#121216] border border-white/10 text-sm text-white outline-none focus:border-[#FF4500]/40" autoFocus />
                            <button data-testid={`chat-edit-save-${m.id}`} onClick={() => saveEdit(m.id)} className="p-1.5 rounded bg-[#FF4500] text-white"><Check className="w-3.5 h-3.5" /></button>
                            <button data-testid={`chat-edit-cancel-${m.id}`} onClick={() => { setEditingId(null); setEditText(''); }} className="p-1.5 rounded bg-white/5 text-[#8A8A93]"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        ) : (
                          <div className="flex items-end gap-1.5 max-w-[85%]">
                            {!m.deleted && (
                              <div className={`flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${mine ? '' : 'order-2'}`}>
                                <button data-testid={`chat-react-${m.id}`} onClick={() => setReactionPickerId(reactionPickerId === m.id ? null : m.id)} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]"><SmilePlus className="w-3 h-3" /></button>
                                {mine && <button data-testid={`chat-edit-${m.id}`} onClick={() => { setEditingId(m.id); setEditText(m.text); }} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]"><Pencil className="w-3 h-3" /></button>}
                                {mine && <button data-testid={`chat-delete-${m.id}`} onClick={() => del(m.id)} className="p-1 rounded hover:bg-white/10 text-red-400"><Trash2 className="w-3 h-3" /></button>}
                              </div>
                            )}
                            <div className={`flex flex-col gap-1 ${mine ? '' : 'order-1'} relative`}>
                              <div className={`px-3 py-2 rounded-2xl text-sm break-words ${m.deleted ? 'italic text-[#55555C] bg-white/[0.03]' : mine ? 'bg-[#FF4500] text-white' : 'bg-white/[0.06] text-white'}`}>
                                {m.deleted ? 'message deleted' : renderText(m.text)}
                              </div>
                              {!m.deleted && (m.scene_refs || []).map(r => (
                                <button key={r.id} data-testid={`chat-scene-ref-${r.id}`} onClick={() => goToScene(r.id)}
                                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#A78BFA]/10 border border-[#A78BFA]/25 text-[#A78BFA] text-xs hover:bg-[#A78BFA]/20 transition-colors w-fit">
                                  <Clapperboard className="w-3.5 h-3.5" /> <span className="truncate max-w-[200px]">{r.title || 'Scene'}</span>
                                </button>
                              ))}
                              {!m.deleted && Object.keys(m.reactions || {}).length > 0 && (
                                <div className={`flex flex-wrap gap-1 ${mine ? 'justify-end' : ''}`} data-testid={`chat-reactions-${m.id}`}>
                                  {Object.entries(m.reactions).map(([emoji, users]) => {
                                    const reacted = users.includes(user.id);
                                    const names = users.map(uid => uid === user.id ? 'You' : (memberNameMap[uid] || 'Someone'));
                                    const tip = `${emoji}  ${names.join(', ')}`;
                                    return (
                                      <button key={emoji} title={tip} data-testid={`chat-reaction-${m.id}-${emoji}`} onClick={() => react(m.id, emoji)}
                                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${reacted ? 'bg-[#FF4500]/15 border-[#FF4500]/40 text-white' : 'bg-white/[0.04] border-white/10 text-[#A1A1AA] hover:bg-white/[0.08]'}`}>
                                        <span>{emoji}</span><span className="text-[10px]">{users.length}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {reactionPickerId === m.id && (
                                <div data-testid={`chat-reaction-picker-${m.id}`} className={`absolute z-10 -top-9 ${mine ? 'right-0' : 'left-0'} flex gap-0.5 bg-[#1A1A20] border border-white/10 rounded-full px-1.5 py-1 shadow-xl`}>
                                  {QUICK_EMOJIS.map(e => (
                                    <button key={e} data-testid={`chat-emoji-${m.id}-${e}`} onClick={() => react(m.id, e)} className="w-6 h-6 rounded-full hover:bg-white/10 text-sm leading-none">{e}</button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selected scene-ref chips (pending send) */}
                {sceneRefs.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 px-3 pt-2" data-testid="chat-pending-scene-refs">
                    {sceneRefs.map(r => (
                      <span key={r.id} className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#A78BFA]/15 text-[#A78BFA] text-[11px]">
                        <Clapperboard className="w-3 h-3" /> {r.title || 'Scene'}
                        <button onClick={() => toggleSceneRef(r)} className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Composer */}
                <div className="relative px-3 py-3 border-t border-white/[0.06] shrink-0">
                  {/* @mention suggestions */}
                  {mentionOpen && filteredMembers.length > 0 && (
                    <div data-testid="chat-mention-menu" className="absolute bottom-full left-3 right-3 mb-1 bg-[#1A1A20] border border-white/10 rounded-lg shadow-xl overflow-hidden">
                      {filteredMembers.map((mem, idx) => {
                        const activeIdx = Math.min(mentionIndex, filteredMembers.length - 1);
                        return (
                          <button key={mem.user_id} data-testid={`chat-mention-option-${mem.user_id}`} onClick={() => pickMention(mem)}
                            onMouseEnter={() => setMentionIndex(idx)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${idx === activeIdx ? 'bg-[#FF4500]/15' : 'hover:bg-white/5'}`}>
                            <span className="w-6 h-6 rounded-full bg-[#A78BFA]/15 text-[#A78BFA] text-[11px] font-bold flex items-center justify-center">{mem.name[0]?.toUpperCase()}</span>
                            <span className="text-sm text-white">{mem.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {/* Scene picker */}
                  {showScenePicker && (
                    <div data-testid="chat-scene-picker" className="absolute bottom-full left-3 right-3 mb-1 max-h-52 overflow-y-auto bg-[#1A1A20] border border-white/10 rounded-lg shadow-xl">
                      {projScenes.length === 0 ? (
                        <p className="text-xs text-[#55555C] px-3 py-3 text-center">No scenes in this project yet.</p>
                      ) : projScenes.map((s, i) => (
                        <button key={s.id} data-testid={`chat-scene-option-${s.id}`} onClick={() => { toggleSceneRef({ id: s.id, title: s.title }); setShowScenePicker(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5">
                          <span className="text-[10px] text-[#55555C] w-5">{i + 1}</span>
                          <Clapperboard className="w-3.5 h-3.5 text-[#A78BFA] shrink-0" />
                          <span className="text-sm text-white truncate">{s.title || `Scene ${i + 1}`}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {active.scope === 'project' && (
                      <button data-testid="chat-scene-ref-btn" onClick={() => { setShowScenePicker(v => !v); setMentionOpen(false); }}
                        title="Reference a scene"
                        className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${showScenePicker ? 'bg-[#A78BFA]/25 text-[#A78BFA]' : 'bg-white/[0.04] text-[#8A8A93] hover:text-white'}`}>
                        <Clapperboard className="w-4 h-4" />
                      </button>
                    )}
                    <input
                      ref={inputRef}
                      data-testid="chat-input"
                      value={text}
                      onChange={e => onChangeText(e.target.value)}
                      onKeyDown={e => {
                        if (mentionOpen && filteredMembers.length) {
                          if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % filteredMembers.length); return; }
                          if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + filteredMembers.length) % filteredMembers.length); return; }
                          if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); pickMention(filteredMembers[Math.min(mentionIndex, filteredMembers.length - 1)]); return; }
                          if (e.key === 'Escape') { e.preventDefault(); setMentionOpen(false); return; }
                        }
                        if (e.key === 'Enter' && !e.shiftKey && !mentionOpen) { e.preventDefault(); send(); }
                        if (e.key === 'Escape') { setMentionOpen(false); setShowScenePicker(false); }
                      }}
                      placeholder="Message… use @ to mention"
                      className="flex-1 px-3 py-2 rounded-full bg-[#121216] border border-white/10 text-sm text-white outline-none focus:border-[#FF4500]/40"
                    />
                    <button data-testid="chat-send-btn" onClick={send} disabled={sending || (!text.trim() && sceneRefs.length === 0)}
                      className="w-9 h-9 shrink-0 rounded-full bg-[#FF4500] text-white flex items-center justify-center hover:bg-[#E03C00] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
